<template>
  <Drawer
    :aria-label="isEdit ? 'Edit endpoint' : 'Add an endpoint'"
    data-cy="webhook-endpoint-form"
    @hide-drawer="emit('close')"
  >
    <template #title>
      <div class="pr-10">
        <h2 class="mb-1 text-base font-semibold text-gray-900">
          {{ isEdit ? "Edit endpoint" : "Add an endpoint" }}
        </h2>
        <p class="m-0 text-sm text-gray-600">
          Sera sends a POST to this URL each time one of the chosen events
          happens.
        </p>
      </div>
    </template>

    <form
      id="webhook-endpoint-form"
      class="space-y-5 px-4 pb-4"
      novalidate
      @submit.prevent="save"
    >
      <Alert
        v-if="baseError"
        type="error"
        data-cy="webhook-form-error"
        :text="baseError"
      />

      <div>
        <label
          for="webhook-endpoint-url"
          class="mb-1 block text-sm font-medium text-gray-900"
          >Endpoint URL</label
        >
        <input
          id="webhook-endpoint-url"
          v-model.trim="url"
          type="url"
          inputmode="url"
          autocomplete="off"
          spellcheck="false"
          placeholder="https://example.com/webhooks/sera"
          class="block w-full rounded-lg border bg-gray-50 p-2.5 text-sm text-gray-900"
          :class="
            fieldError('url')
              ? 'border-red-500 focus:border-red-500'
              : 'border-gray-300 focus:border-blue-500'
          "
          :aria-invalid="!!fieldError('url')"
          aria-describedby="webhook-endpoint-url-help"
          data-cy="webhook-form-url"
        />
        <p
          v-if="fieldError('url')"
          id="webhook-endpoint-url-help"
          class="m-0 mt-1 text-xs text-red-700"
          data-cy="webhook-form-url-error"
        >
          {{ fieldError("url") }}
        </p>
        <p
          v-else
          id="webhook-endpoint-url-help"
          class="m-0 mt-1 text-xs text-gray-500"
        >
          An https URL on a public server. Treat it like a password: anyone who
          has it can send requests to it.
        </p>
      </div>

      <div>
        <label
          for="webhook-endpoint-description"
          class="mb-1 block text-sm font-medium text-gray-900"
          >Description
          <span class="font-normal text-gray-500">(optional)</span></label
        >
        <input
          id="webhook-endpoint-description"
          v-model="description"
          type="text"
          maxlength="255"
          placeholder="What receives these events, for example Accounting sync"
          class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500"
          :aria-invalid="!!fieldError('description')"
          data-cy="webhook-form-description"
        />
        <p
          v-if="fieldError('description')"
          class="m-0 mt-1 text-xs text-red-700"
        >
          {{ fieldError("description") }}
        </p>
      </div>

      <fieldset>
        <legend class="mb-1 text-sm font-medium text-gray-900">Events</legend>
        <p class="m-0 mb-2 text-xs text-gray-500">
          Choose the events this endpoint receives.
        </p>
        <p
          v-if="fieldError('event_types')"
          class="m-0 mb-2 text-xs text-red-700"
          data-cy="webhook-form-events-error"
        >
          {{ fieldError("event_types") }}
        </p>
        <div
          v-for="group in groups"
          :key="group.group"
          class="mb-3 overflow-hidden rounded-lg border border-gray-200"
          :data-cy="`webhook-form-group:${group.group}`"
        >
          <div
            class="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase text-gray-500"
          >
            {{ group.group }}
          </div>
          <div
            v-for="eventType in group.eventTypes"
            :key="eventType.event_type"
            class="border-b border-gray-100 px-3 py-2 last:border-b-0"
          >
            <label class="flex cursor-pointer items-start gap-2 text-sm">
              <input
                v-model="selectedTypes"
                type="checkbox"
                class="mt-1"
                :value="eventType.event_type"
                :data-cy="`webhook-form-event:${eventType.event_type}`"
              />
              <span>
                <span class="font-medium text-gray-900">{{
                  eventType.name
                }}</span>
                <code class="ml-1 text-xs text-gray-500">{{
                  eventType.event_type
                }}</code>
                <span class="block text-xs text-gray-600">{{
                  eventType.description
                }}</span>
              </span>
            </label>
            <button
              type="button"
              class="link ml-6 text-xs"
              :aria-expanded="openSample === eventType.event_type"
              :data-cy="`webhook-form-sample-toggle:${eventType.event_type}`"
              @click="toggleSample(eventType.event_type)"
            >
              {{
                openSample === eventType.event_type
                  ? "Hide example"
                  : "Show example"
              }}
            </button>
            <JsonViewer
              v-if="openSample === eventType.event_type"
              class="ml-6 mt-2"
              :value="eventType.sample"
              title="Example payload"
            />
          </div>
        </div>
      </fieldset>
    </form>

    <template #footer>
      <div class="flex w-full justify-end gap-2 px-4 py-3">
        <button
          type="button"
          class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          data-cy="webhook-form-cancel"
          @click="emit('close')"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="webhook-endpoint-form"
          class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
          :disabled="saving"
          data-cy="webhook-form-save"
        >
          {{ saving ? "Saving…" : isEdit ? "Save changes" : "Add endpoint" }}
        </button>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import JsonViewer from "@/components/JsonViewer.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import type {
  WebhookEndpoint,
  WebhookEventType
} from "@/vuex/modules/integrationWebhooks";
import { apiError, groupEventTypes } from "./webhooksHub";

const props = withDefaults(
  defineProps<{
    eventTypes: WebhookEventType[];
    // Edit when given: the show response, which carries the full url.
    endpoint?: WebhookEndpoint | null;
  }>(),
  { endpoint: null }
);
const emit = defineEmits<{ close: []; saved: [endpoint: WebhookEndpoint] }>();

const store = useStore();

const isEdit = computed(() => !!props.endpoint);
const url = ref(props.endpoint?.url ?? "");
const description = ref(props.endpoint?.description ?? "");
const selectedTypes = ref<string[]>([...(props.endpoint?.event_types ?? [])]);
const openSample = ref<string | null>(null);
const saving = ref(false);
const baseError = ref("");
const fieldErrors = ref<Record<string, string[]>>({});

const groups = computed(() => groupEventTypes(props.eventTypes));

// The API sends attribute messages ("couldn't be found. Check the URL for
// typos"), so each is read with its field's name in front.
const FIELD_SUBJECTS: Record<string, string> = {
  url: "This URL",
  description: "The description",
  event_types: "The event list"
};

function fieldError(field: string): string {
  return (fieldErrors.value[field] ?? [])
    .map((message) =>
      /^[a-z]/.test(message) && FIELD_SUBJECTS[field]
        ? `${FIELD_SUBJECTS[field]} ${message}`
        : message
    )
    .join(" ");
}

function toggleSample(eventType: string) {
  openSample.value = openSample.value === eventType ? null : eventType;
}

// Only the obvious mistakes are caught here; the API checks the URL for real
// (https on 443, resolves to a public address) and its messages are shown
// as they come.
function validate(): boolean {
  const errors: Record<string, string[]> = {};
  if (!url.value) {
    errors.url = ["Enter the URL Sera should send events to."];
  } else if (!/^https:\/\//i.test(url.value)) {
    errors.url = ["Use an https URL."];
  }
  if (!selectedTypes.value.length) {
    errors.event_types = ["Choose at least one event."];
  }
  fieldErrors.value = errors;
  return !Object.keys(errors).length;
}

async function save() {
  baseError.value = "";
  if (!validate()) return;

  saving.value = true;
  const params = {
    url: url.value,
    description: description.value.trim(),
    event_types: selectedTypes.value
  };
  try {
    const saved: WebhookEndpoint = isEdit.value
      ? await store.dispatch("updateWebhookEndpoint", {
          id: props.endpoint!.id,
          ...params
        })
      : await store.dispatch("createWebhookEndpoint", params);
    window.notify.success(isEdit.value ? "Endpoint saved." : "Endpoint added.");
    emit("saved", saved);
  } catch (error) {
    const parsed = apiError(
      error,
      "The endpoint couldn't be saved. Try again."
    );
    fieldErrors.value = parsed.fieldErrors;
    const fieldsShown = ["url", "description", "event_types"].some(
      (field) => parsed.fieldErrors[field]?.length
    );
    baseError.value = parsed.fieldErrors.base?.length
      ? parsed.fieldErrors.base.join(" ")
      : fieldsShown
        ? ""
        : parsed.message;
  } finally {
    saving.value = false;
  }
}
</script>
